import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../utils/api';
import {
  RefreshCw, Plus, Edit3, Trash2, X, Check, Search, BookOpen,
  Tag, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_OPTIONS = [
  'Getting Started', 'Compliance', 'Business Growth', 'Grants',
  'Load Board Strategies', 'Safety', 'Technology', 'General'
];

const EMPTY_FORM = { title: '', content: '', category: 'General', tags: '', premium: false };

export const ResourceManagementSection = () => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchResources = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/resources');
      setResources(res.data.resources || []);
    } catch {
      toast.error('Failed to load resources');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchResources(); }, [fetchResources]);

  const openCreate = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (resource) => {
    setEditingId(resource.id);
    setFormData({
      title: resource.title,
      content: resource.content,
      category: resource.category,
      tags: (resource.tags || []).join(', '),
      premium: resource.premium || false,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    const payload = {
      title: formData.title.trim(),
      content: formData.content.trim(),
      category: formData.category,
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      premium: formData.premium,
    };
    try {
      if (editingId) {
        await api.put(`/admin/resources/${editingId}`, payload);
        toast.success('Resource updated');
      } else {
        await api.post('/admin/resources', payload);
        toast.success('Resource created');
      }
      setShowForm(false);
      setEditingId(null);
      fetchResources();
    } catch {
      toast.error('Failed to save resource');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resource? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/resources/${id}`);
      toast.success('Resource deleted');
      fetchResources();
    } catch {
      toast.error('Failed to delete resource');
    }
  };

  const categories = [...new Set(resources.map(r => r.category))];
  const filtered = resources.filter(r => {
    const matchSearch = !searchTerm || r.title.toLowerCase().includes(searchTerm.toLowerCase()) || r.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = categoryFilter === 'all' || r.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  return (
    <div className="space-y-4" data-testid="resource-management-section">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text" placeholder="Search resources..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            data-testid="resource-search-input"
          />
        </div>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
          className="bg-white/5 border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="resource-category-filter"
        >
          <option value="all" className="bg-slate-900">All Categories</option>
          {categories.map(c => <option key={c} value={c} className="bg-slate-900">{c}</option>)}
        </select>
        <button onClick={openCreate}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          data-testid="add-resource-btn"
        >
          <Plus className="h-4 w-4" /> Add Resource
        </button>
      </div>

      {/* Create / Edit Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="bg-white/5 border border-white/10 rounded-xl p-5"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-sm">{editingId ? 'Edit Resource' : 'Create New Resource'}</h3>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Title *</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Resource title" data-testid="resource-title-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Category</label>
                  <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    data-testid="resource-category-input"
                  >
                    {CATEGORY_OPTIONS.map(c => <option key={c} value={c} className="bg-slate-900">{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Content *</label>
                <textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                  placeholder="Resource content or description..." data-testid="resource-content-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Tags (comma separated)</label>
                  <input type="text" value={formData.tags} onChange={(e) => setFormData({...formData, tags: e.target.value})}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="safety, compliance, tips" data-testid="resource-tags-input"
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer p-2" data-testid="resource-premium-toggle">
                    <input type="checkbox" checked={formData.premium} onChange={(e) => setFormData({...formData, premium: e.target.checked})}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-300">Premium resource</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
                <button type="submit" disabled={saving}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="save-resource-btn"
                >
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  {editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Resources Table */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-white font-medium text-sm">All Resources</h3>
          <span className="text-slate-500 text-xs">{filtered.length} of {resources.length}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="h-10 w-10 text-slate-600 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">{resources.length === 0 ? 'No resources yet. Create the first one.' : 'No matching resources.'}</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map((resource) => (
              <div key={resource.id} className="px-4 py-3 hover:bg-white/5 transition-colors" data-testid={`resource-row-${resource.id}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-white text-sm font-medium truncate">{resource.title}</p>
                      {resource.premium && (
                        <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded text-[10px] font-medium">
                          <Lock className="h-2.5 w-2.5" />Premium
                        </span>
                      )}
                    </div>
                    <p className="text-slate-500 text-xs truncate">{resource.content}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] px-1.5 py-0.5 bg-blue-500/15 text-blue-400 rounded font-medium">{resource.category}</span>
                      {(resource.tags || []).slice(0, 3).map(tag => (
                        <span key={tag} className="flex items-center gap-0.5 text-[10px] text-slate-500"><Tag className="h-2.5 w-2.5" />{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(resource)}
                      className="p-2 text-slate-400 hover:text-blue-400 rounded-lg transition-colors" title="Edit"
                      data-testid={`edit-resource-${resource.id}`}>
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(resource.id)}
                      className="p-2 text-slate-400 hover:text-red-400 rounded-lg transition-colors" title="Delete"
                      data-testid={`delete-resource-${resource.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="px-4 py-3 border-t border-white/10 text-slate-500 text-xs">
          {resources.length} total resources
        </div>
      </div>
    </div>
  );
};
